/**
 * Checkout Shipping Popup Handler
 * Date Modified: 2026-02-05
 * Author: Claude Code
 * Purpose: Display promotional popup for 50% off domestic shipping before checkout
 * Scope: Intercepts checkout buttons for logged-in customers only, coordinates with login popup
 * Modified: Added shipping rate estimate functionality using Shopify Cart Ajax API
 */

class CheckoutShippingPopup extends HTMLElement {
  constructor() {
    super();
    this.enablePopup = this.dataset.enablePopup === 'true';
    this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
    this.popupHeading = this.dataset.popupHeading;
    this.popupMessage = this.dataset.popupMessage;
    this.continueText = this.dataset.continueText;

    this.overlay = this.querySelector('.shipping-popup-overlay');
    this.closeButton = this.querySelector('[data-close-popup]');
    this.continueButton = this.querySelector('[data-continue-button]');

    // Shipping estimate elements
    this.shippingSection = this.querySelector('[data-shipping-estimate]');
    this.shippingResult = this.querySelector('.shipping-estimate-result');
    this.shippingError = this.querySelector('[data-shipping-error]');
    this.zipInput = this.querySelector('#shipping-zip-input');
    this.calculateBtn = this.querySelector('[data-calculate-shipping]');
    this.originalPriceEl = this.querySelector('[data-original-price]');
    this.savingsAmountEl = this.querySelector('[data-savings-amount]');

    // Customer address data
    this.customerZip = this.dataset.customerZip || '';
    this.customerCountry = this.dataset.customerCountry || 'United States';
    this.customerProvince = this.dataset.customerProvince || '';

    // Threshold data for dynamic updates
    this.minimumAmountCents = parseInt(this.dataset.minimumAmountCents) || 0;
    this.reachedThreshold = this.dataset.reachedThreshold === 'true';
    this.shippingRatesFetched = false;

    this.pendingCheckoutAction = null;
    this.init();
  }

  init() {
    if (!this.enablePopup) {
      // Don't attach event listeners if popup is disabled
      return;
    }

    this.attachEventListeners();
    this.setupKeyboardNavigation();
    this.listenForLoginPopupTrigger();
    this.setupShippingCalculator();
    this.listenForCartUpdates();
  }

  /**
   * Listen for cart update events to dynamically show shipping estimate
   * Date Modified: 2026-02-05
   * Author: Claude Code
   * Purpose: Show shipping estimate when cart reaches threshold without page reload
   */
  listenForCartUpdates() {
    // Listen for the theme's cart update event
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail?.cart);
    });

    // Also listen for FoxTheme pubsub if available
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.handleCartUpdate(event.cart);
      });
    }
  }

  handleCartUpdate(cart) {
    if (!cart || !this.minimumAmountCents) return;

    const cartTotal = cart.total_price || cart.items_subtotal_price || 0;
    const wasReached = this.reachedThreshold;
    this.reachedThreshold = cartTotal >= this.minimumAmountCents;

    // Update shipping estimate section visibility
    if (this.shippingSection) {
      this.shippingSection.hidden = !this.reachedThreshold;
    }

    // If threshold was just reached and we haven't fetched rates yet, fetch them
    if (this.reachedThreshold && !wasReached && this.customerZip && !this.shippingRatesFetched) {
      this.fetchShippingRates(this.customerZip);
    }
  }

  setupShippingCalculator() {
    // Set up shipping calculator event listeners
    if (this.calculateBtn) {
      this.calculateBtn.addEventListener('click', () => this.fetchShippingRates());
    }
    if (this.zipInput) {
      this.zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.fetchShippingRates();
        }
      });
    }
  }

  attachEventListeners() {
    // Listen for checkout button clicks globally
    document.addEventListener('click', this.handleCheckoutClick.bind(this));
    document.addEventListener('submit', this.handleCheckoutSubmit.bind(this));

    // Popup interaction handlers
    this.overlay?.addEventListener('click', this.handleOverlayClick.bind(this));
    this.closeButton?.addEventListener('click', this.hidePopup.bind(this));
    this.continueButton?.addEventListener('click', this.proceedToCheckout.bind(this));
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (event) => {
      if (this.getAttribute('aria-hidden') === 'false') {
        if (event.key === 'Escape') {
          this.hidePopup();
        }

        // Trap focus within popup
        if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      }
    });
  }

  listenForLoginPopupTrigger() {
    /**
     * Listen for custom event from login popup
     * When guest user clicks "Continue to Checkout" on login popup,
     * that popup dispatches this event so we can show the shipping popup
     */
    document.addEventListener('checkout-login-continue', (event) => {
      // Guest user chose to continue without logging in
      // Now show them the shipping promotion popup
      this.pendingCheckoutAction = event.detail?.checkoutAction || 'navigate';

      // Small delay to allow login popup to close smoothly
      setTimeout(() => this.showPopup(), 300);
    });
  }

  handleCheckoutClick(event) {
    const target = event.target;

    // Don't intercept clicks within our popup
    if (target.closest('checkout-shipping-popup')) {
      return;
    }

    // Check if clicked element is a checkout button
    if (this.isCheckoutButton(target)) {
      // CRITICAL: Only intercept if customer is logged in
      // If not logged in, let the login popup handle it
      if (!this.customerLoggedIn) {
        // Let login popup handle it
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // Store the action for later execution
      this.pendingCheckoutAction = {
        type: 'click',
        element: target
      };

      this.showPopup();
      return false;
    }
  }

  handleCheckoutSubmit(event) {
    const form = event.target;

    // Don't intercept form submits within our popup
    if (form.closest('checkout-shipping-popup')) {
      return;
    }

    // Check if this is a cart form submission
    if (this.isCheckoutForm(form)) {
      // CRITICAL: Only intercept if customer is logged in
      if (!this.customerLoggedIn) {
        // Let login popup handle it
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // Store the form submission action
      this.pendingCheckoutAction = {
        type: 'submit',
        form: form
      };

      this.showPopup();
      return false;
    }
  }

  isCheckoutButton(element) {
    // CRITICAL: Must exclude add-to-cart buttons and only catch checkout buttons

    // First check if element or any parent is an add-to-cart button - exclude these
    if (element.closest('button[name="add"], button[type="submit"]:not([name="checkout"]), form[action*="/cart/add"]')) {
      return false;
    }

    // Check if element itself or any parent is a checkout button
    const checkoutSelectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[type="submit"][name="checkout"]',
      'a[href="/checkout"]',
      'a[href*="/checkouts/"]'
    ];

    // Check if element matches OR if it's inside a checkout button/link
    return checkoutSelectors.some(selector => {
      return (element.matches && element.matches(selector)) || element.closest(selector);
    });
  }

  isCheckoutForm(form) {
    // Check if form is submitting to checkout
    const action = form.getAttribute('action') || '';

    // Exclude add-to-cart forms explicitly
    if (action.includes('/cart/add') || action.includes('/cart/change')) {
      return false;
    }

    // Only match cart form that contains a checkout button
    // This ensures we only catch the actual cart checkout form
    return (action === '/cart' || action.endsWith('/cart')) && form.querySelector('button[name="checkout"]');
  }

  showPopup() {
    // First close any open cart drawer
    this.closeCartDrawer();

    // Show popup after a brief delay to allow cart drawer to close
    setTimeout(() => {
      this.setAttribute('aria-hidden', 'false');

      // Prevent scrolling on the main body
      document.body.classList.add('shipping-popup-open');
      document.body.style.overflow = 'hidden';

      // Focus the first focusable element
      this.focusFirstElement();

      // Announce to screen readers
      this.announceToScreenReader('Shipping promotion popup opened. 50% off domestic shipping.');

      // Auto-fetch shipping rates if customer has address
      if (this.customerZip) {
        setTimeout(() => this.fetchShippingRates(this.customerZip), 500);
      }
    }, 200);
  }

  closeCartDrawer() {
    // Close cart drawer - check if it's open first
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Check if cart drawer is currently open
      if (cartDrawer.getAttribute('aria-hidden') === 'false' || !cartDrawer.hasAttribute('hidden')) {
        if (typeof cartDrawer.hide === 'function') {
          cartDrawer.hide();
        }
      }
    }

    // Alternative method for cart drawer with ID
    const cartDrawerElement = document.querySelector('#CartDrawer');
    if (cartDrawerElement && !cartDrawerElement.hidden) {
      // Look for specific cart close button
      const closeButton = cartDrawerElement.querySelector('button[aria-controls="CartDrawer"], .drawer__close, [data-close-drawer]');
      if (closeButton) {
        closeButton.click();
      }
    }

    // Close cart drawer overlay
    const cartOverlay = document.querySelector('.cart-drawer .fixed-overlay, .drawer--right .fixed-overlay');
    if (cartOverlay) {
      cartOverlay.click();
    }
  }

  handleOverlayClick(event) {
    // Prevent the click from bubbling up to document
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();

    // Only close if clicking directly on the overlay, not on child elements
    if (event.target === this.overlay) {
      this.hidePopup();
    }
  }

  hidePopup() {
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('shipping-popup-open');
    document.body.style.overflow = '';

    // Clear pending action
    this.pendingCheckoutAction = null;

    // Return focus to the trigger element if possible
    this.returnFocus();

    // Announce to screen readers
    this.announceToScreenReader('Shipping promotion popup closed');
  }

  proceedToCheckout() {
    // Hide the popup
    this.hidePopup();

    // Navigate to Shopify checkout
    window.location.href = '/checkout';
  }

  focusFirstElement() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  trapFocus(event) {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  returnFocus() {
    // Try to return focus to the element that triggered the popup
    if (this.pendingCheckoutAction && this.pendingCheckoutAction.element) {
      this.pendingCheckoutAction.element.focus();
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Fetch shipping rates from Shopify Cart API
   * Date Modified: 2026-02-05
   * Author: Claude Code
   * Purpose: Get estimated shipping cost to display 50% savings
   */
  async fetchShippingRates(zip = null) {
    const zipCode = zip || (this.zipInput ? this.zipInput.value : '') || this.customerZip;
    if (!zipCode) {
      this.showShippingError('Please enter a zip code');
      return;
    }

    this.showShippingLoading();

    try {
      const response = await fetch(`${FoxTheme.routes.cart_url}/shipping_rates.json`, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: JSON.stringify({
          shipping_address: {
            zip: zipCode,
            country: this.customerCountry,
            province: this.customerProvince
          }
        })
      });

      const data = await response.json();

      if (data.shipping_rates && data.shipping_rates.length > 0) {
        this.displayShippingRates(data.shipping_rates[0]);
      } else if (data.errors) {
        this.showShippingError(data.errors);
      } else {
        this.showShippingError('No shipping rates available');
      }
    } catch (error) {
      console.error('Shipping rate fetch error:', error);
      this.showShippingError('Unable to calculate shipping');
    }
  }

  displayShippingRates(rate) {
    // API returns the already-discounted rate (50% off applied)
    // To show original, we multiply by 2; savings equals the discounted rate
    const discountedPrice = parseFloat(rate.price);
    const discountedCents = discountedPrice * 100;
    const originalCents = discountedCents * 2; // Original before 50% discount
    const savingsCents = discountedCents; // Customer saves this amount

    const formattedOriginal = FoxTheme.Currency.formatMoney(originalCents, FoxTheme.settings.moneyFormat);
    const formattedSavings = FoxTheme.Currency.formatMoney(savingsCents, FoxTheme.settings.moneyFormat);

    // Use textContent for security
    if (this.originalPriceEl) {
      this.originalPriceEl.textContent = formattedOriginal;
    }
    if (this.savingsAmountEl) {
      this.savingsAmountEl.textContent = formattedSavings;
    }

    this.hideShippingLoading();
    if (this.shippingResult) {
      this.shippingResult.hidden = false;
    }
    if (this.shippingError) {
      this.shippingError.hidden = true;
    }

    // Mark rates as fetched to prevent duplicate API calls
    this.shippingRatesFetched = true;
  }

  showShippingLoading() {
    const loadingEl = this.querySelector('.shipping-estimate-loading');
    const formEl = this.querySelector('.shipping-estimate-form');
    if (loadingEl) loadingEl.hidden = false;
    if (formEl) formEl.hidden = true;
    if (this.shippingResult) this.shippingResult.hidden = true;
    if (this.shippingError) this.shippingError.hidden = true;
  }

  hideShippingLoading() {
    const loadingEl = this.querySelector('.shipping-estimate-loading');
    if (loadingEl) loadingEl.hidden = true;
  }

  showShippingError(message) {
    this.hideShippingLoading();
    const formEl = this.querySelector('.shipping-estimate-form');
    if (formEl) formEl.hidden = false;
    if (this.shippingResult) this.shippingResult.hidden = true;
    if (this.shippingError) {
      this.shippingError.hidden = false;
      this.shippingError.textContent = typeof message === 'string' ? message : 'Unable to calculate shipping';
    }
  }
}

// Register the custom element
if (!customElements.get('checkout-shipping-popup')) {
  customElements.define('checkout-shipping-popup', CheckoutShippingPopup);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Additional initialization if needed
  console.log('Checkout Shipping Popup initialized');
});
